/**
 * Prim Export Modal — mirrors Tahsilat export experience:
 *  - Format: XLSX | CSV
 *  - Dışarı aktarılacak sütunlar (checkbox grid)
 *  - Takımca uygulanan sütun filtreleri (cf) + yil/ay bilgisi aktarıma dahil
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Download, FileSpreadsheet, Columns3, ChevronRight, ChevronDown, Filter } from "lucide-react";
import { API, getToken } from "@/lib/api";

// Mirrors backend PRIM_EXPORT_FIELDS in prim_import_export.py
export const PRIM_EXPORT_COLUMNS = [
  { k: "tarih",             l: "Tarih",                 t: "date" },
  { k: "prim_ayi_label",    l: "Prim Ayı",              t: "text" },
  { k: "icra_foy",          l: "İcra Föy",              t: "text" },
  { k: "icra_esas",         l: "İcra Esas",             t: "text" },
  { k: "dosya_adi",         l: "Dosya Adı",             t: "text" },
  { k: "alacakli_adi",      l: "Müvekkil Adı",          t: "text" },
  { k: "varlik_sirketi",    l: "Varlık Şti.",           t: "bool" },
  { k: "sehir_disi",        l: "Şehir İçi/Dışı",        t: "bool" },
  { k: "oran",              l: "Oran (Oto, rozetli)",   t: "percent" },
  { k: "vekalet_ucreti",    l: "Vekalet Ücreti (₺)",    t: "money" },
  { k: "tahsilat_miktari",  l: "Tahsilat Miktarı (₺)",  t: "money" },
  { k: "odenecek_toplam",   l: "Ödenecek Miktar (₺)",   t: "money" },
  { k: "giden_1",           l: "Giden Kişi 1",          t: "text" },
  { k: "giden_2",           l: "Giden Kişi 2",          t: "text" },
  { k: "giden_3",           l: "Giden Kişi 3",          t: "text" },
  { k: "giden_4",           l: "Giden Kişi 4",          t: "text" },
  { k: "giden_sayisi",      l: "Giden Sayısı",          t: "num" },
  { k: "kisi_basi_prim",    l: "Kişi Başı Prim (₺)",    t: "money" },
  { k: "arastiran_isim",    l: "Araştıran Kişi",        t: "text" },
  { k: "arastirma_primi",   l: "Araştırma Primi (₺)",   t: "money" },
  { k: "aciklama",          l: "Açıklama",              t: "text" },
];

export default function PrimExportModal({ yil, ay, filterAll, cfString, cfCount = 0, onClose }) {
  const [format, setFormat] = useState("xlsx");
  const [downloading, setDownloading] = useState(false);
  const [selectedCols, setSelectedCols] = useState(() => new Set(PRIM_EXPORT_COLUMNS.map((c) => c.k)));
  const [colsOpen, setColsOpen] = useState(false);

  const toggleCol = (k) => setSelectedCols((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const selectAll = () => setSelectedCols(new Set(PRIM_EXPORT_COLUMNS.map((c) => c.k)));
  const clearAll  = () => setSelectedCols(new Set());

  const periodLabel = useMemo(() => {
    if (filterAll) return "Tüm Dönemler";
    return `${String(ay).padStart(2, "0")}/${yil}`;
  }, [filterAll, yil, ay]);

  const handleDownload = async () => {
    if (selectedCols.size === 0) { toast.error("En az bir sütun seçmelisiniz"); return; }
    const params = new URLSearchParams();
    if (!filterAll) { params.set("yil", String(yil)); params.set("ay", String(ay)); }
    if (cfString) params.set("cf", cfString);
    params.set("format", format);
    if (selectedCols.size < PRIM_EXPORT_COLUMNS.length) {
      const ordered = PRIM_EXPORT_COLUMNS.filter((c) => selectedCols.has(c.k)).map((c) => c.k);
      params.set("columns", ordered.join(","));
    }
    setDownloading(true);
    try {
      const url = `${API}/prim/export?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      const ext = format === "csv" ? "csv" : "xlsx";
      a.setAttribute("download", `prim_${filterAll ? "tumu" : yil + "_" + String(ay).padStart(2, "0")}.${ext}`);
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("İndirildi");
      onClose?.();
    } catch {
      toast.error("Dışa aktarılamadı");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !downloading && onClose?.()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="prim-export-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-bold text-slate-900 inline-flex items-center gap-2"><Download size={16}/> Prim Listesi Dışa Aktar</div>
          <button onClick={() => onClose?.()} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-[11px] bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
            <span className="font-semibold text-slate-700">Dönem:</span>{" "}
            <span className="text-slate-900">{periodLabel}</span>
            {cfCount > 0 && (
              <>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-indigo-700 inline-flex items-center gap-1">
                  <Filter size={10}/> {cfCount} sütun filtresi aktarıma dahil
                </span>
              </>
            )}
          </div>

          {/* Format */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">Format</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormat("xlsx")}
                data-testid="prim-export-format-xlsx"
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                  format === "xlsx" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                <FileSpreadsheet size={16} /> Excel
              </button>
              <button
                type="button"
                onClick={() => setFormat("csv")}
                data-testid="prim-export-format-csv"
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                  format === "csv" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                title="UTF-8 BOM + ; ayracı (Excel-TR ve muhasebe programları için)"
              >
                <FileSpreadsheet size={16} /> CSV
              </button>
            </div>
            {format === "csv" && (
              <div className="mt-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                CSV: Excel-TR uyumlu (<code>;</code> ayracı, UTF-8 BOM, virgüllü ondalık).
              </div>
            )}
          </div>

          {/* Column selection */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="prim-export-columns-panel">
            <button
              type="button"
              onClick={() => setColsOpen((x) => !x)}
              className="w-full flex items-center justify-between text-left"
              data-testid="prim-export-columns-toggle"
            >
              <div className="flex items-center gap-2">
                <Columns3 size={14} className="text-indigo-600" />
                <span className="text-sm font-semibold text-slate-800">Dışarı Aktarılacak Sütunlar</span>
                <span className="text-[11px] font-bold bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">
                  {selectedCols.size}/{PRIM_EXPORT_COLUMNS.length}
                </span>
              </div>
              {colsOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            </button>
            {colsOpen && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={selectAll} className="text-[11px] font-semibold text-indigo-700 hover:underline" data-testid="prim-export-cols-all">Tümünü Seç</button>
                  <span className="text-slate-300">·</span>
                  <button type="button" onClick={clearAll} className="text-[11px] font-semibold text-rose-600 hover:underline" data-testid="prim-export-cols-clear">Hiçbiri</button>
                  <span className="ml-auto text-[11px] text-slate-500">Çıktıda seçili sütunlar yer alır (sıra sabit)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded p-2">
                  {PRIM_EXPORT_COLUMNS.map((c) => (
                    <label key={c.k} className="flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50 px-1.5 py-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCols.has(c.k)}
                        onChange={() => toggleCol(c.k)}
                        data-testid={`prim-export-col-${c.k}`}
                      />
                      <span className="flex-1 truncate" title={c.l}>{c.l}</span>
                      <span className="text-[9px] text-slate-400">{c.t}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={downloading}
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || selectedCols.size === 0}
            className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-xs font-semibold rounded-md inline-flex items-center gap-1.5"
            data-testid="prim-export-download-btn"
          >
            <Download size={13} /> {downloading ? "İndiriliyor..." : "İndir"}
          </button>
        </div>
      </div>
    </div>
  );
}
